import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Search, Filter, UserPlus, UserMinus } from 'lucide-react';
import { AccommodationLog } from '../types/accommodation';
import { useAccommodationStore } from '../stores/accommodationStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useLanguage } from '../contexts/LanguageContext';

export const AccommodationLogsPage = () => {
  const { translations } = useLanguage();
  const { tables, loading, error, fetchTables } = useAccommodationStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHotel, setSelectedHotel] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">{error}</div>;

  // Extract all logs from all tables and rooms
  const allLogs: AccommodationLog[] = Object.values(tables).flatMap(table =>
    table.rooms.flatMap(room => room.logs || [])
  );

  // Sort logs by timestamp in descending order
  const sortedLogs = [...allLogs].sort((a, b) => b.timestamp - a.timestamp);

  // Get unique hotels for filter
  const hotels = Array.from(new Set(Object.values(tables).map(table => table.hotelName)));

  // Filter logs
  const filteredLogs = sortedLogs.filter(log => {
    const matchesSearch = 
      !searchTerm ||
      log.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.guideName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.organizerName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesHotel = selectedHotel === 'all' || log.hotelName === selectedHotel;
    const matchesAction = selectedAction === 'all' || log.action === selectedAction;
    const matchesRole = selectedRole === 'all' || log.userRole === selectedRole;

    return matchesSearch && matchesHotel && matchesAction && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-50 rounded-lg">
          <Clock className="text-primary-500" size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.accommodationLogs?.header || 'Activity Logs'}
          </h1>
          <p className="text-gray-500 mt-1">
            {translations?.accommodationLogs?.subheader || 'Tracking accommodation activities in hotels'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={translations?.accommodationLogs?.searchPlaceholder || 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {/* Hotel Filter */}
          <select
            value={selectedHotel}
            onChange={(e) => setSelectedHotel(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.accommodationLogs?.filters?.allHotels || 'All Hotels'}
            </option>
            {hotels.map(hotel => (
              <option key={hotel} value={hotel}>{hotel}</option>
            ))}
          </select>

          {/* Action Filter */}
          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value as 'all' | 'assign' | 'remove')}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.accommodationLogs?.filters?.allActions || 'All Actions'}
            </option>
            <option value="assign">
              {translations?.accommodationLogs?.filters?.assign || 'Assign'}
            </option>
            <option value="remove">
              {translations?.accommodationLogs?.filters?.remove || 'Remove'}
            </option>
          </select>

          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.accommodationLogs?.filters?.allRoles || 'All Roles'}
            </option>
            <option value="admin">
              {translations?.accommodationLogs?.filters?.admin || 'Admin'}
            </option>
            <option value="guide">
              {translations?.accommodationLogs?.filters?.guide || 'Guide'}
            </option>
            <option value="organizer">
              {translations?.accommodationLogs?.filters?.organizer || 'Organizer'}
            </option>
          </select>
        </div>

        {/* Active Filters */}
        {(searchTerm || selectedHotel !== 'all' || selectedAction !== 'all' || selectedRole !== 'all') && (
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">
              {translations?.accommodationLogs?.activeFilters || 'Active Filters:'}
            </span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.accommodationLogs?.filters?.search || 'Search'}: {searchTerm}
                </span>
              )}
              {selectedHotel !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.accommodationLogs?.filters?.hotel || 'Hotel'}: {selectedHotel}
                </span>
              )}
              {selectedAction !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.accommodationLogs?.filters?.action || 'Action'}:{' '}
                  {selectedAction === 'assign'
                    ? translations?.accommodationLogs?.filters?.assign || 'Assign'
                    : translations?.accommodationLogs?.filters?.remove || 'Remove'}
                </span>
              )}
              {selectedRole !== 'all' && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.accommodationLogs?.filters?.role || 'Role'}:{' '}
                  {selectedRole === 'admin'
                    ? translations?.accommodationLogs?.filters?.admin || 'Admin'
                    : selectedRole === 'guide'
                    ? translations?.accommodationLogs?.filters?.guide || 'Guide'
                    : translations?.accommodationLogs?.filters?.organizer || 'Organizer'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Logs List */}
      <div className="space-y-4">
        {filteredLogs.map((log) => (
          <motion.div
            key={log.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-4 rounded-lg shadow-sm"
          >
            <div className="flex items-start gap-4">
              <div
                className={`p-2 rounded-lg shrink-0 ${
                  log.action === 'assign'
                    ? 'bg-green-100 text-green-600'
                    : 'bg-red-100 text-red-600'
                }`}
              >
                {log.action === 'assign' ? (
                  <UserPlus size={20} />
                ) : (
                  <UserMinus size={20} />
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {log.action === 'assign'
                      ? translations?.accommodationLogs?.logActions?.assign || 'Assigned'
                      : translations?.accommodationLogs?.logActions?.remove || 'Removed'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500 mb-1">
                      {translations?.accommodationLogs?.logDetails?.pilgrimInfo || 'Pilgrim Info'}
                    </div>
                    <div className="font-medium">{log.pilgrimName}</div>
                    <div className="text-gray-600">
                      {log.guideName} → {log.organizerName}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500 mb-1">
                      {translations?.accommodationLogs?.logDetails?.accommodationInfo || 'Accommodation Info'}
                    </div>
                    <div className="font-medium">{log.hotelName}</div>
                    <div className="text-gray-600">
                      {translations?.accommodationLogs?.logDetails?.room || 'Room'} {log.roomNumber} -{' '}
                      {translations?.accommodationLogs?.logDetails?.bed || 'Bed'} {log.bedIndex + 1}
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-sm">
                  <span className="text-gray-500">
                    {translations?.accommodationLogs?.logDetails?.performedBy || 'Performed by:'}{' '}
                  </span>
                  <span className="font-medium">{log.userName}</span>
                  <span className="text-gray-400 mx-2">•</span>
                  <span className="text-gray-600">
                    {log.userRole === 'admin'
                      ? translations?.accommodationLogs?.filters?.admin || 'Admin'
                      : log.userRole === 'guide'
                      ? translations?.accommodationLogs?.filters?.guide || 'Guide'
                      : translations?.accommodationLogs?.filters?.organizer || 'Organizer'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}

        {filteredLogs.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              {translations?.accommodationLogs?.noLogs || 'No logs match the selected filters'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};